<script lang="ts" setup>
import { tpgIconDragHandle, tpgIconRemoveCircle } from '@tpg/grammar-web-icons';
import { computed, inject, toRefs } from 'vue';

import {
  GrammarBadge,
  GrammarDivider,
  GrammarListItem,
  GrammarQuaternaryButton,
} from '@nora-monorepo/grammar-ui';
import type { NamedLatLonCoordinate } from '@nora-monorepo/shared';

import { useMapStore } from '#nora-ui/map/stores/map-store';

interface Props {
  deletePoint: (index: number) => void;
  coordinate: NamedLatLonCoordinate;
  index: number;
  isError?: boolean;
}

const props = defineProps<Props>();
const { coordinate, deletePoint } = toRefs(props);

// Get mapId from context - it should be provided by parent component
const mapId = inject<string>('mapId');
const mapStore = useMapStore();

function formatCoordinate(coordinate: NamedLatLonCoordinate) {
  return `${coordinate.latitude.toFixed(6)}, ${coordinate.longitude.toFixed(6)}`;
}

function navigateToLocation() {
  if (!mapId) {
    return;
  }
  const map = mapStore.getMapById(mapId);
  if (map) {
    map.setCenter({
      latitude: coordinate.value.latitude,
      longitude: coordinate.value.longitude,
    });
  }
}

const displayValue = computed(() => {
  if (coordinate.value.name) {
    return coordinate.value.name;
  }

  return formatCoordinate(coordinate.value);
});
</script>

<template>
  <GrammarListItem>
    <template #header-leading-buttons>
      <div class="flex min-w-0 flex-1 items-center">
        <GrammarBadge
          :label="`${index + 1}.`"
          @click="navigateToLocation"
        />

        <GrammarBadge
          :label="displayValue"
          :type="isError ? 'error' : 'neutral'"
          class="grow"
          emphasis="no-background-high"
          size="lg"
        />
      </div>
    </template>
    <template #header-trailing-buttons>
      <GrammarQuaternaryButton
        :icon-template="tpgIconDragHandle"
        class="drag-handle"
        size="small"
      />

      <GrammarDivider orientation="vertical" />

      <GrammarQuaternaryButton
        :icon-template="tpgIconRemoveCircle"
        size="small"
        @click="deletePoint(index)"
      />
    </template>
  </GrammarListItem>
</template>
